import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  Stack,
} from '@mui/material';
import {
  SMS_OPT_IN_CONSENT_TEXT,
  PRIVACY_POLICY_VERSION,
  TERMS_VERSION,
  smsOptInSchema,
} from '../lib/validation';
import { submitSmsOptIn } from '../lib/api';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  smsConsent: boolean;
}

const initialFormData: FormData = {
  fullName: '',
  email: '',
  phone: '',
  smsConsent: false,
};

interface Props {
  turnstileToken: string;
}

function SmsOptInForm({ turnstileToken }: Props) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, smsConsent: e.target.checked }));
    if (errors.smsConsent) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.smsConsent;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSubmitStatus(null);

    const dataToValidate = {
      ...formData,
      smsConsent: formData.smsConsent as true,
      turnstileToken,
    };

    const result = smsOptInSchema.safeParse(dataToValidate);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await submitSmsOptIn({
        ...result.data,
        smsConsentText: SMS_OPT_IN_CONSENT_TEXT,
        privacyPolicyVersion: PRIVACY_POLICY_VERSION,
        termsVersion: TERMS_VERSION,
        sourceUrl: '/sms-opt-in',
      } as Parameters<typeof submitSmsOptIn>[0]);

      if (response.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Thank you! You have successfully opted in to receive SMS updates from Gorty\'s Tech, LLC.',
        });
        setFormData(initialFormData);
      } else {
        setSubmitStatus({
          type: 'error',
          message: response.message,
        });
      }
    } catch {
      setSubmitStatus({
        type: 'error',
        message: 'An unexpected error occurred. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === 'success') {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        {submitStatus.message}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        {submitStatus?.type === 'error' && (
          <Alert severity="error">{submitStatus.message}</Alert>
        )}

        <TextField
          required
          fullWidth
          label="Phone Number"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          error={!!errors.phone}
          helperText={errors.phone || 'Enter your phone number to receive SMS updates'}
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Email (Optional)"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Full Name (Optional)"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          disabled={isSubmitting}
        />

        <Box className="consent-box">
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.smsConsent}
                onChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {SMS_OPT_IN_CONSENT_TEXT}
              </Typography>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
          {errors.smsConsent && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {errors.smsConsent}
            </Typography>
          )}
        </Box>

        {errors.turnstileToken && (
          <Alert severity="error">{errors.turnstileToken}</Alert>
        )}

        <Box id="turnstile-widget-sms" sx={{ minHeight: 65 }}>
          {/* Turnstile widget will be rendered here */}
        </Box>

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting}
          sx={{ py: 1.5 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Opt In to SMS Updates'
          )}
        </Button>
      </Stack>
    </Box>
  );
}

export default SmsOptInForm;
